import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Activity, 
  TrendingUp, 
  Shield, 
  Globe, 
  Users, 
  Server, 
  Wifi,
  AlertCircle,
  CheckCircle,
  Clock,
  BarChart3,
  Zap,
  Lock,
  Network
} from 'lucide-react';

interface DataMetrics {
  healthConditions: number;
  medications: number;
  foodItems: number;
  nutritionData: number;
  labResults: number;
  symptoms: number;
  devices: number;
  users: number;
  totalRecords: number;
}

interface SystemStatus {
  databaseConnections: number;
  activeTables: number;
  uptime: string;
  lastSync: string;
  networkLatency: number;
  encryptionStatus: 'active' | 'warning' | 'error';
  p2pNodes: number;
  cloudServers: number;
}

interface OutageInfo {
  hasOutage: boolean;
  affectedServices: string[];
  estimatedResolution: string;
  status: 'operational' | 'degraded' | 'outage';
}

export default function DataMetricsDisplay() {
  const [metrics, setMetrics] = useState<DataMetrics>({
    healthConditions: 0,
    medications: 0,
    foodItems: 0,
    nutritionData: 0,
    labResults: 0,
    symptoms: 0,
    devices: 0,
    users: 0,
    totalRecords: 0
  });

  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    databaseConnections: 0,
    activeTables: 0,
    uptime: '0h 0m',
    lastSync: 'Never',
    networkLatency: 0,
    encryptionStatus: 'active',
    p2pNodes: 0,
    cloudServers: 0
  });

  const [outageInfo, setOutageInfo] = useState<OutageInfo>({
    hasOutage: false,
    affectedServices: [],
    estimatedResolution: '',
    status: 'operational'
  });

  const [showDetails, setShowDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Simulate real-time data updates
  useEffect(() => {
    const updateMetrics = () => {
      // Simulate realistic data metrics
      setMetrics({
        healthConditions: 113137, // FDA NDC products
        medications: 103610, // Legacy medications
        foodItems: 697115, // USDA branded foods
        nutritionData: 750000, // Total nutrition entries
        labResults: 50000, // Lab result templates
        symptoms: 25000, // Symptom database
        devices: 15000, // Device integrations
        users: 12500, // Active users
        totalRecords: 1650000 // Total data points
      });
    };

    const updateSystemStatus = () => {
      setSystemStatus({
        databaseConnections: Math.floor(Math.random() * 50) + 100,
        activeTables: 12,
        uptime: `${Math.floor(Math.random() * 24)}h ${Math.floor(Math.random() * 60)}m`,
        lastSync: new Date().toLocaleTimeString(),
        networkLatency: Math.floor(Math.random() * 50) + 20,
        encryptionStatus: 'active' as const,
        p2pNodes: Math.floor(Math.random() * 20) + 5,
        cloudServers: 8
      });
    };

    const updateOutageInfo = () => {
      // Simulate occasional minor issues
      const hasIssue = Math.random() < 0.1;
      setOutageInfo({
        hasOutage: hasIssue,
        affectedServices: hasIssue ? ['Data Sync'] : [],
        estimatedResolution: hasIssue ? '5 minutes' : '',
        status: hasIssue ? 'degraded' as const : 'operational' as const
      });
    };

    // Initial load
    updateMetrics();
    updateSystemStatus();
    updateOutageInfo();
    setIsLoading(false);

    // Update every 30 seconds
    const interval = setInterval(() => {
      updateMetrics();
      updateSystemStatus();
      updateOutageInfo();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational': return 'text-green-600 bg-green-50 border-green-200';
      case 'degraded': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'outage': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getEncryptionIcon = () => {
    switch (systemStatus.encryptionStatus) {
      case 'active': return <Shield className="h-4 w-4 text-green-600" />;
      case 'warning': return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'error': return <AlertCircle className="h-4 w-4 text-red-600" />;
      default: return <Shield className="h-4 w-4 text-gray-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Metrics Display */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Your Health Data Access</h3>
            <p className="text-slate-600 text-sm">
              Real-time access to our comprehensive health database
            </p>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
        </div>

        {/* System Status Banner */}
        <div className={`mb-6 p-4 rounded-lg border ${getStatusColor(outageInfo.status)}`}>
          <div className="flex items-center space-x-2">
            {outageInfo.status === 'operational' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <div>
              <p className="font-medium">
                System Status: {outageInfo.status.charAt(0).toUpperCase() + outageInfo.status.slice(1)}
              </p>
              {outageInfo.hasOutage && (
                <p className="text-sm">
                  {outageInfo.affectedServices.join(', ')} - Resolving in {outageInfo.estimatedResolution}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Data Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Activity className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600">{formatNumber(metrics.healthConditions)}</p>
            <p className="text-sm text-blue-700">Health Conditions</p>
          </div>

          <div className="text-center p-4 bg-green-50 rounded-lg">
            <Database className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600">{formatNumber(metrics.medications)}</p>
            <p className="text-sm text-green-700">Medications</p>
          </div>

          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <BarChart3 className="h-8 w-8 text-orange-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-orange-600">{formatNumber(metrics.foodItems)}</p>
            <p className="text-sm text-orange-700">Food Items</p>
          </div>

          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <TrendingUp className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-600">{formatNumber(metrics.totalRecords)}</p>
            <p className="text-sm text-purple-700">Total Records</p>
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-lg font-semibold text-slate-700">{formatNumber(metrics.nutritionData)}</p>
            <p className="text-xs text-slate-600">Nutrition Data</p>
          </div>

          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-lg font-semibold text-slate-700">{formatNumber(metrics.labResults)}</p>
            <p className="text-xs text-slate-600">Lab Results</p>
          </div>

          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-lg font-semibold text-slate-700">{formatNumber(metrics.symptoms)}</p>
            <p className="text-xs text-slate-600">Symptoms</p>
          </div>
        </div>
      </div>

      {/* Detailed System Status */}
      {showDetails && (
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h4 className="text-lg font-semibold text-slate-800 mb-4">System Infrastructure</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Database Status */}
            <div>
              <h5 className="font-medium text-slate-700 mb-3">Database & Storage</h5>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Active Connections</span>
                  <span className="font-medium">{systemStatus.databaseConnections}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Active Tables</span>
                  <span className="font-medium">{systemStatus.activeTables}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Uptime</span>
                  <span className="font-medium">{systemStatus.uptime}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Last Sync</span>
                  <span className="font-medium">{systemStatus.lastSync}</span>
                </div>
              </div>
            </div>

            {/* Network Status */}
            <div>
              <h5 className="font-medium text-slate-700 mb-3">Network & Security</h5>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Network Latency</span>
                  <span className="font-medium">{systemStatus.networkLatency}ms</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Encryption Status</span>
                  <div className="flex items-center space-x-1">
                    {getEncryptionIcon()}
                    <span className="font-medium capitalize">{systemStatus.encryptionStatus}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">P2P Nodes</span>
                  <span className="font-medium">{systemStatus.p2pNodes}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Cloud Servers</span>
                  <span className="font-medium">{systemStatus.cloudServers}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Infrastructure Highlights */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
            <h5 className="font-medium text-slate-800 mb-2">Infrastructure Highlights</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="text-slate-700">HIPAA Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <Lock className="h-4 w-4 text-blue-600" />
                <span className="text-slate-700">End-to-End Encryption</span>
              </div>
              <div className="flex items-center space-x-2">
                <Network className="h-4 w-4 text-purple-600" />
                <span className="text-slate-700">P2P Redundancy</span>
              </div>
              <div className="flex items-center space-x-2">
                <Server className="h-4 w-4 text-orange-600" />
                <span className="text-slate-700">Redundant Servers</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="h-4 w-4 text-yellow-600" />
                <span className="text-slate-700">Real-Time Sync</span>
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4 text-indigo-600" />
                <span className="text-slate-700">Global CDN</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Benefits */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
        <h4 className="text-lg font-semibold text-slate-800 mb-3">What This Means for You</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-start space-x-2">
            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
            <span className="text-slate-700">
              Access to {formatNumber(metrics.totalRecords)}+ health data points
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
            <span className="text-slate-700">
              Real-time updates from FDA, USDA, and medical databases
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
            <span className="text-slate-700">
              Secure, encrypted storage with redundant backups
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
            <span className="text-slate-700">
              We handle all the heavy lifting of data management
            </span>
          </div>
        </div>
      </div>
    </div>
  );
} 